import { Pregnancy, CreatePregnancyDto, UpdatePregnancyDto, QueryPregnancyDto } from '@/core/models/pregnancy.model'

import { cacher } from '@/utils/rtkQueryCacheUtils'
import { baseApi, Tag } from './base'

export const pregnancyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPregnancies: builder.query<Pregnancy[], QueryPregnancyDto | void>({
      query: (params) => ({
        url: '/pregnancies',
        method: 'GET',
        params: params || {},
      }),
      providesTags: cacher.providesList(Tag.PREGNANCY),
    }),

    getPregnanciesByPatient: builder.query<Pregnancy[], string>({
      query: (patientId) => ({
        url: '/pregnancies',
        method: 'GET',
        params: { patient: patientId },
      }),
      providesTags: (result, error, patientId) => [
        { type: Tag.PREGNANCY, id: `patient-${patientId}` },
        { type: Tag.PREGNANCY, id: 'LIST' },
      ],
    }),

    getActivePregnancy: builder.query<Pregnancy | null, string>({
      query: (patientId) => ({
        url: '/pregnancies',
        method: 'GET',
        params: { patient: patientId, status: 'active' },
      }),
      transformResponse: (response: Pregnancy[]) => response[0] ?? null,
      providesTags: (result, error, patientId) => [
        { type: Tag.PREGNANCY, id: `active-${patientId}` },
        { type: Tag.PREGNANCY, id: 'LIST' },
      ],
    }),

    getPregnancy: builder.query<Pregnancy, string>({
      query: (id) => `/pregnancies/${id}`,
      providesTags: (result) => (result ? [{ type: Tag.PREGNANCY, id: result._id }] : []),
    }),

    createPregnancy: builder.mutation<Pregnancy, CreatePregnancyDto>({
      query: (body) => ({
        url: '/pregnancies',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        { type: Tag.PREGNANCY, id: 'LIST' },
        { type: Tag.PREGNANCY, id: `patient-${body.patient}` },
        { type: Tag.PREGNANCY, id: `active-${body.patient}` },
      ],
    }),

    updatePregnancy: builder.mutation<Pregnancy, { id: string; body: UpdatePregnancyDto }>({
      query: ({ id, body }) => ({
        url: `/pregnancies/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: Tag.PREGNANCY, id: result._id },
              { type: Tag.PREGNANCY, id: 'LIST' },
              { type: Tag.PREGNANCY, id: `patient-${typeof result.patient === 'string' ? result.patient : result.patient}` },
              { type: Tag.PREGNANCY, id: `active-${typeof result.patient === 'string' ? result.patient : result.patient}` },
            ]
          : [{ type: Tag.PREGNANCY, id: 'LIST' }],
    }),

    deletePregnancy: builder.mutation<Pregnancy, { id: string; patientId: string }>({
      query: ({ id }) => ({
        url: `/pregnancies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { patientId }) => [
        { type: Tag.PREGNANCY, id: 'LIST' },
        { type: Tag.PREGNANCY, id: `patient-${patientId}` },
        { type: Tag.PREGNANCY, id: `active-${patientId}` },
      ],
    }),
  }),
  overrideExisting: false,
})
